import { ChangeDetectionStrategy, Component, EventEmitter, OnInit, Output } from '@angular/core';
import { DatePipe } from '@angular/common';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BaseComponent } from '@shared-features/components/base-component/base.component';
import { TabModel } from '@shared/models';
import { BidChatLogsState, BidChatService } from '@features/bid-chat/services/bid-chat.service';
import { BidChatRow, ChatConversationStatus, ChatSenderType } from '@features/bid-chat/models/bid-chat.model';
import { CUSTOMER_CHAT_SIDE, offerPreviewPrice } from '@features/bid-chat/constants/bid-chat.constants';
import { BidChatDrawerService } from '@features/bid-chat/services/bid-chat-drawer.service';

enum ChatLogsTab {
  Active = 0,
  History = 1,
}

const SENDER_LABEL_KEYS: Record<ChatSenderType, string> = {
  [ChatSenderType.Fleet]: 'bidChat.FleetLabel',
  [ChatSenderType.Driver]: 'bidChat.DriverLabel',
  [ChatSenderType.Customer]: 'bidChat.CustomerLabel',
  [ChatSenderType.IndividualDriver]: 'bidChat.DriverLabel',
};

/** The customer's Chat logs in the chat drawer (#47189), per the "Chat logs" design. */
@Component({
  selector: 'app-chat-logs',
  templateUrl: './chat-logs.component.html',
  styleUrls: ['./chat-logs.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DatePipe],
  // eslint-disable-next-line @angular-eslint/no-host-metadata-property
  host: { class: 'flex column gap-3 p-6' },
})
export class ChatLogsComponent extends BaseComponent implements OnInit {
  @Output() rowSelected = new EventEmitter<BidChatRow>();
  @Output() closed = new EventEmitter<void>();

  logs$!: Observable<BidChatLogsState>;
  selectedTab = ChatLogsTab.Active;

  readonly chatLogsTab = ChatLogsTab;
  readonly openStatus = ChatConversationStatus.Open;
  readonly tabs: TabModel[] = [{ label: 'ActiveChats' }, { label: 'History' }];
  readonly skeletonRows = [1, 2];

  private readonly searchTerm$ = new BehaviorSubject<string>('');

  constructor(private bidChatService: BidChatService, private drawerService: BidChatDrawerService, private datePipe: DatePipe) {
    super();
    // Back from a thread lands on the tab it was opened from.
    this.selectedTab = this.drawerService.logsTab;
  }

  onTabChange(index: number): void {
    this.selectedTab = index;
    this.drawerService.logsTab = index;
  }

  /**
   * An offer's preview is stored in English ("Offer: 1200 SAR"); the row shows it translated, with
   * the riyal symbol. `null` = a text preview, shown as is.
   */
  offerPreviewPrice(row: BidChatRow): number | null {
    return offerPreviewPrice(row.lastMessagePreview);
  }

  ngOnInit(): void {
    this.logs$ = combineLatest([this.bidChatService.logs$(), this.searchTerm$]).pipe(
      map(([logs, term]) => ({ ...logs, active: this.filterRows(logs.active, term), closed: this.filterRows(logs.closed, term) }))
    );
  }

  onSearch(term: string | null): void {
    this.searchTerm$.next((term || '').trim().toLowerCase());
  }

  trackByConversation(_index: number, row: BidChatRow): string {
    return row.firestoreConversationId;
  }

  /** "You:" when the customer sent it, else the sender's role (contract §7.1); no sender type → no label. */
  senderLabel(row: BidChatRow): string {
    if (!row.lastMessageSenderType) return '';
    return row.lastMessageSenderType === CUSTOMER_CHAT_SIDE.senderType
      ? this.translate('bidChat.YouLabel')
      : this.translate(SENDER_LABEL_KEYS[row.lastMessageSenderType] ?? SENDER_LABEL_KEYS[ChatSenderType.Fleet]);
  }

  /** Today → time, yesterday → "Yesterday", older → day and month, in local time (contract §7.1). */
  rowTime(row: BidChatRow): string {
    const value = row.lastMessageAt ?? row.createdAt;
    if (!value) return '';

    const date = new Date(value);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return this.datePipe.transform(date, 'hh:mm a') ?? '';
    if (date.toDateString() === yesterday.toDateString()) return this.translate('bidChat.Yesterday');
    return this.datePipe.transform(date, 'd MMM') ?? '';
  }

  unreadBadge(row: BidChatRow): string {
    return row.unreadCount > 99 ? '99+' : `${row.unreadCount}`;
  }

  /** Client-side: both sources are already the customer's whole inbox, capped at 50. */
  private filterRows(rows: BidChatRow[], term: string): BidChatRow[] {
    if (!term) return rows;
    return rows.filter((row) =>
      [row.shipmentId, row.fromCity, row.toCity, row.counterpartName, row.lastMessagePreview].some((value) =>
        (value || '').toString().toLowerCase().includes(term)
      )
    );
  }
}
